import { format } from 'date-fns';
import { enGB, type Locale, nl } from 'date-fns/locale';

// The venue timezone - all events are in Rotterdam, Netherlands
export const VENUE_TIMEZONE = 'Europe/Amsterdam';

export const getDateLocale = (locale: string): Locale => {
    return locale.startsWith('nl') ? nl : enGB;
};

/**
 * Get the month (0-indexed) in the venue's timezone.
 * Useful for color coding by month, etc.
 */
export const getVenueMonth = (date: Date | string): number => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: VENUE_TIMEZONE,
        month: 'numeric',
    });
    return Number.parseInt(formatter.format(dateObj), 10) - 1; // 0-indexed
};

/**
 * Convert a date to the venue's timezone (Europe/Amsterdam).
 * Returns a Date object that, when formatted with date-fns, shows the venue's local time.
 */
const toVenueTime = (date: Date | string): Date => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Use Intl.DateTimeFormat to get the date parts in the venue timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: VENUE_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(dateObj);
    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';

    // Create a new Date using the venue's local time values
    // This "tricks" date-fns into formatting as if we're in the venue timezone
    return new Date(
        Number.parseInt(getPart('year'), 10),
        Number.parseInt(getPart('month'), 10) - 1, // 0-indexed
        Number.parseInt(getPart('day'), 10),
        Number.parseInt(getPart('hour'), 10),
        Number.parseInt(getPart('minute'), 10)
    );
};

/**
 * ISO 8601 string in the venue's local time with its UTC offset
 * (e.g. 2026-09-28T19:30:00+02:00), for structured data.
 */
export const toVenueIsoString = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: VENUE_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'longOffset',
    }).formatToParts(dateObj);
    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';
    // longOffset renders as "GMT+02:00" (or plain "GMT" for a zero offset)
    const offset = getPart('timeZoneName').replace('GMT', '') || '+00:00';

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}${offset}`;
};

export const formatDate = {
    // Common date formats
    dayMonth: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'd MMM', { locale: dateLocale });
    },

    dayMonthYear: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'EEE d MMM yyyy', { locale: dateLocale });
    },

    fullDayMonthYear: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'EEEE d MMM yyyy', { locale: dateLocale });
    },

    monthLongWithYear: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'MMMM yyyy', { locale: dateLocale });
    },

    dayShort: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'dd', { locale: dateLocale });
    },

    monthShort: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'MMM', { locale: dateLocale });
    },

    time: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'HH:mm', { locale: dateLocale });
    },

    // For calendar comparison
    compareFormat: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'yyyy-MM-dd', { locale: dateLocale });
    },

    dateTime: (date: Date | string, locale: string): string => {
        const dateObj = toVenueTime(date);
        const dateLocale = getDateLocale(locale);
        return format(dateObj, 'dd MMM yyyy HH:mm', { locale: dateLocale });
    },

    /**
     * Format a local date without timezone conversion.
     * Use this for dates that already represent the intended calendar date
     * (e.g., dates from react-calendar which represent abstract calendar dates).
     */
    fullDayMonthYearLocal: (date: Date, locale: string): string => {
        const dateLocale = getDateLocale(locale);
        return format(date, 'EEEE d MMM yyyy', { locale: dateLocale });
    },
};
